import { type ClipsListResponse, Items, type PublicClip, ShortUrl } from '@air/api';
import {
  type Board,
  type Clip,
  type ClipAndBoardListItem,
  type ClipAndBoardListOptions,
  ClipType,
  type ListResponse,
  type PublicBoard,
  SortDirection,
  type ViewField,
} from '@air/api/types';

import { PageLimits } from '~/constants/PageLimits';
import { privateClipsFetcher, publicClipsFetcher } from '~/swr-hooks/gallery/galleryView/utils';
import { type UseGalleryMixedData } from '~/swr-hooks/gallery/types';

type MediaPlayerClipRef = {
  id: Clip['id'];
  assetId: Clip['assetId'];
};

const handleTableViewClipsData = <C extends Clip | PublicClip>({
  pagination,
  data,
}: ClipsListResponse<C>): ListResponse<ClipAndBoardListItem> => ({
  data: data.clips.map((clip) => ({
    id: clip.id,
    type: clip.type === ClipType.nonMedia ? 'file' : 'asset',
    data: clip,
  })),
  total: data.total,
  pagination,
});

export const privateTableViewClipsDataFetcher = async ({
  params,
  sortFieldName,
  sortDirection,
  cursor,
  workspaceId,
  collectionId,
}: {
  cursor: string | null;
  params: Partial<ClipAndBoardListOptions>;
  sortFieldName?: ViewField['name'];
  sortDirection?: SortDirection;
  workspaceId: string;
  /** Scopes a drilled board's clip list to the collection it is viewed within (FOUND-840). */
  collectionId?: string;
}): Promise<ListResponse<ClipAndBoardListItem>> => {
  const scopedParams = collectionId
    ? {
        ...params,
        filters: {
          ...params.filters,
          collection: { is: collectionId, scope: 'all' },
        },
      }
    : params;

  // biome-ignore format: @ts-expect-error only covers the next line
  // @ts-expect-error - mismatched type of sortBy, but ClipAndBoardListSortBy is subset of ClipsListSortBy
  const response = await privateClipsFetcher({ cursor, params: scopedParams, sortFieldName, sortDirection, workspaceId });

  return handleTableViewClipsData(response);
};

export const publicTableViewClipsDataFetcher = async ({
  shortId,
  parentId,
  params,
  sortFieldName,
  sortDirection,
  cursor,
  collectionId,
}: {
  cursor: string | null;
  shortId: string;
  parentId: string;
  params: Partial<ClipAndBoardListOptions>;
  sortFieldName: ViewField['name'];
  sortDirection: SortDirection;
  /** See `getCollectionScopedClipParams` — absent outside a collection sub-page drill. */
  collectionId?: string;
}): Promise<ListResponse<ClipAndBoardListItem>> => {
  const response = await publicClipsFetcher({
    shortId,
    parentId,
    // @ts-expect-error - mismatched type of sortBy, but ClipAndBoardListSortBy is subset of ClipsListSortBy
    params,
    sortDirection,
    sortFieldName,
    cursor,
    collectionId,
  });

  return handleTableViewClipsData(response);
};

/** The caller's explicit limit wins; otherwise the first page loads more than follow-up pages. */
const getTableViewFetchLimit = ({ limit, cursor }: { limit?: number; cursor?: string | null }) => {
  if (limit) {
    return limit;
  }

  return !cursor ? PageLimits.files.init : PageLimits.files.more;
};

export const publicTableViewDataFetcher = async ({
  shortId,
  params,
  sortFieldName,
  sortDirection,
  cursor,
  collectionId,
}: {
  cursor?: string;
  shortId: string;
  params: Partial<ClipAndBoardListOptions>;
  sortFieldName?: ViewField['name'];
  sortDirection?: SortDirection;
  /**
   * The collection a drilled board is being viewed within (a sub-page's collectionId). Absent on
   * the standalone board link and root-collection drills, where the backend's root default is
   * correct. See `getCollectionScopedClipParams` for the scope semantics this mirrors.
   */
  collectionId?: string;
}): Promise<ListResponse<ClipAndBoardListItem>> => {
  const response = await ShortUrl.searchClipsAndBoardsByShortId(shortId, {
    ...params,
    cursor,
    includeAncestors: true,
    includeDirectDescendantCount: true,
    limit: getTableViewFetchLimit({ limit: params.limit, cursor }),
    sortField: {
      name: sortFieldName,
      direction: sortDirection || SortDirection.asc,
    },
    ...(collectionId && {
      filters: {
        ...params.filters,
        // `scope: 'all'`: a drilled board's assets are held by boards, i.e. indirect collection
        // members. Without this filter the backend scopes to the link's ROOT collection.
        collection: { is: collectionId, scope: 'all' },
      },
    }),
  });

  return response;
};

export const privateTableViewDataFetcher = async ({
  params,
  sortFieldName,
  sortDirection,
  cursor,
  workspaceId,
  collectionId,
}: {
  cursor: string | null;
  params: Partial<ClipAndBoardListOptions>;
  sortFieldName?: ViewField['name'];
  sortDirection?: SortDirection;
  workspaceId: string;
  /** Scopes a drilled board's mixed list to the collection it is viewed within (FOUND-840). */
  collectionId?: string;
}): Promise<ListResponse<ClipAndBoardListItem>> => {
  const response = await Items.searchClipsAndBoards({
    workspaceId,
    options: {
      ...params,
      cursor,
      includeAncestors: true,
      includeDirectDescendantCount: true,
      limit: getTableViewFetchLimit({ limit: params.limit, cursor }),
      sortField: {
        name: sortFieldName,
        direction: sortDirection || SortDirection.asc,
      },
      ...(collectionId && {
        filters: {
          ...params.filters,
          collection: { is: collectionId, scope: 'all' },
        },
      }),
    },
  });

  return response;
};

export const tableViewDataToUpdateMediaPlayer = <B extends Board | PublicBoard, C extends Clip | PublicClip>(
  itemsData: UseGalleryMixedData<B, C>,
) => ({
  itemsIds: itemsData.data.reduce<MediaPlayerClipRef[]>((acc, curr) => {
    if (curr.type === 'file' || curr.type === 'asset') {
      const { id, assetId } = curr.item;
      acc.push({ id, assetId });
    }

    return acc;
  }, []),
  loadMore: itemsData.loadNextPage,
  hasMore: itemsData.hasMore,
});
